import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useBillingSubscription } from "./use-billing-subscription";
import { getActiveCampaigns } from "@/config/campaigns";
import type { Campaign } from "@/config/campaigns";

interface UseTrialUpgradeReturn {
  // Trial status
  isTrialActive: boolean;
  daysRemaining: number;
  progressPercentage: number;
  
  // Upgrade state
  isUpgrading: boolean;
  
  // Actions
  handleUpgrade: (planType?: 'monthly' | 'annual') => Promise<void>;
  shouldShowUpgrade: boolean;
}

export function useTrialUpgrade(): UseTrialUpgradeReturn {
  const { user } = useAuth();
  const { isTrialing: isTrialActive, trialEndsAt, isSubscribed: subscribed } = useBillingSubscription();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);

  // Check for active campaigns
  useEffect(() => {
    const campaigns = getActiveCampaigns();
    if (campaigns.length > 0) {
      setActiveCampaign(campaigns[0]);
    }
  }, []);

  // Calculate progress percentage (0-100)
  // Supports both 30-day and 180-day trials dynamically
  const daysRemaining = trialEndsAt ? 
    Math.ceil((trialEndsAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;
  
  // Infer trial length: if > 30 days remaining, it's likely a 180-day trial
  const inferredTrialLength = daysRemaining > 30 ? 180 : 30;
  const progressPercentage = Math.max(0, Math.min(100, ((inferredTrialLength - daysRemaining) / inferredTrialLength) * 100));

  // Determine if we should show upgrade prompt
  const shouldShowUpgrade = isTrialActive && !subscribed && daysRemaining > 0;

  const handleUpgrade = useCallback(async (planType: 'monthly' | 'annual' = 'monthly') => {
    if (!user || isUpgrading) return;

    setIsUpgrading(true);
    try {
      // Get referral code from Refgrow cookie
      const { getRefgrowReferralCode } = await import('@/lib/refgrow');
      const referralCode = getRefgrowReferralCode();

      // Build checkout data with campaign if active
      const checkoutData: any = {
        planType: activeCampaign ? 'annual' : planType, // Use annual for campaign
        successUrl: `${window.location.origin}/settings/plans`,
        cancelUrl: `${window.location.origin}/settings/plans`,
        referralCode
      };

      // Auto-apply campaign coupon if available
      if (activeCampaign) {
        checkoutData.couponId = activeCampaign.couponId;
        checkoutData.campaign = activeCampaign.metadata.campaign;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: checkoutData
      });
      
      if (error) {
        console.error('Error creating checkout:', error);
        toast.error('Failed to create checkout session');
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create checkout session');
    } finally {
      setIsUpgrading(false);
    }
  }, [user, isUpgrading, activeCampaign]);

  return {
    isTrialActive,
    daysRemaining,
    progressPercentage,
    isUpgrading,
    handleUpgrade,
    shouldShowUpgrade,
  };
} 